import React, { useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { addDays, Alert, btnPrimary, btnSecondary, formatDate, Modal } from "./ui";

const displayDate = (value) => value.split("-").reverse().join("-");

export default function DateRangePicker({ start, end, onChange }) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(start.slice(0, 7));
  const [draftStart, setDraftStart] = useState(start);
  const [draftEnd, setDraftEnd] = useState(end);
  const [choosingEnd, setChoosingEnd] = useState(false);
  const days = Math.round((Date.parse(draftEnd) - Date.parse(draftStart)) / 86400000) + 1;
  const error = days > 62 ? "Date range cannot exceed 62 days." : "";
  const monthDate = new Date(`${month}-01T00:00:00Z`);
  const offset = (monthDate.getUTCDay() + 6) % 7;
  const lastDay = new Date(Date.UTC(monthDate.getUTCFullYear(), monthDate.getUTCMonth() + 1, 0)).getUTCDate();
  const moveMonth = (delta) => {
    const next = new Date(`${month}-01T00:00:00Z`);
    next.setUTCMonth(next.getUTCMonth() + delta);
    setMonth(next.toISOString().slice(0, 7));
  };
  const selectDate = (value) => {
    if (!choosingEnd) {
      setDraftStart(value);
      setDraftEnd(value);
      setChoosingEnd(true);
    } else {
      setDraftStart(value < draftStart ? value : draftStart);
      setDraftEnd(value < draftStart ? draftStart : value);
      setChoosingEnd(false);
    }
  };

  return <>
    <div className="inline-flex w-fit max-w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-slate-200 px-2 py-1 text-sm">
      <span className="whitespace-nowrap"><span className="text-slate-500">Start date: </span><span aria-label="Start date">{displayDate(start)}</span></span>
      <span className="whitespace-nowrap"><span className="text-slate-500">End date: </span><span aria-label="End date">{displayDate(end)}</span></span>
      <button type="button" className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-700 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-800" aria-label="Choose date range" onClick={() => {
        setDraftStart(start);
        setDraftEnd(end);
        setMonth(start.slice(0, 7));
        setChoosingEnd(false);
        setOpen(true);
      }}><CalendarDays size={18} /></button>
    </div>
    <Modal open={open} title="Choose appointment date range" onClose={() => setOpen(false)}>
      <p className="text-sm text-slate-600" aria-live="polite">{choosingEnd ? "Select the end date." : "Select a start date, then an end date. Select the same day twice for a single day."}</p>
      <div className="flex items-center justify-between">
        <button type="button" className={btnSecondary} aria-label="Previous month" onClick={() => moveMonth(-1)}><ChevronLeft size={16} /></button>
        <span aria-live="polite" className="font-semibold">{monthDate.toLocaleDateString("en", { month: "long", year: "numeric", timeZone: "UTC" })}</span>
        <button type="button" className={btnSecondary} aria-label="Next month" onClick={() => moveMonth(1)}><ChevronRight size={16} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1" role="group" aria-label="Calendar dates">
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((day) => <span key={day} className="text-center text-xs text-slate-500">{day}</span>)}
        {Array.from({ length: offset }, (_, index) => <span key={`blank-${index}`} />)}
        {Array.from({ length: lastDay }, (_, index) => {
          const value = addDays(`${month}-01`, index);
          const selected = value >= draftStart && value <= draftEnd;
          const boundary = value === draftStart || value === draftEnd;
          return <button key={value} type="button" aria-label={value} aria-pressed={selected} onClick={() => selectDate(value)}
            className={`min-h-10 rounded-md text-sm ${boundary ? "bg-cyan-800 text-white" : selected ? "bg-cyan-50 text-cyan-900" : "text-slate-800 hover:bg-slate-100"}`}>{index + 1}</button>;
        })}
      </div>
      <p className="text-sm">{formatDate(draftStart)} – {formatDate(draftEnd)}</p>
      <Alert>{error}</Alert>
      <div className="flex justify-end gap-2">
        <button type="button" className={btnSecondary} onClick={() => setOpen(false)}>Cancel</button>
        <button type="button" className={btnPrimary} disabled={Boolean(error) || choosingEnd} onClick={() => {
          onChange(draftStart, draftEnd);
          setOpen(false);
        }}>Apply date range</button>
      </div>
    </Modal>
  </>;
}
